import { decryptBackup, encryptBackup, mergeProviderCareEpisodeUpdate, type CareEpisodeRecord, type CarePassportRecord, type Pet } from '@petrhythm/core';
import { repository } from '@petrhythm/data';

const CARE_SHARE_STATE_KEY = 'petrhythm.care_share_state';

export interface CareShareState {
  petId: string;
  episodeId: string;
  token: string;
  inviteId?: string;
  role: 'guardian' | 'provider';
  actorLabel: string;
  cursor: number;
}

interface CareSharePacket {
  schemaVersion: 1;
  pet: Pick<Pet, 'id' | 'name' | 'species'>;
  passport?: CarePassportRecord;
  episode: CareEpisodeRecord;
}

export interface CareShareAuthor {
  role: 'guardian' | 'provider';
  label: string;
  createdAt: number;
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

export function readCareShareState(): CareShareState | null {
  try {
    const value = JSON.parse(localStorage.getItem(CARE_SHARE_STATE_KEY) ?? 'null') as Partial<CareShareState> | null;
    if (!value || typeof value.petId !== 'string' || typeof value.episodeId !== 'string' || typeof value.token !== 'string' || (value.role !== 'guardian' && value.role !== 'provider')) return null;
    return { petId: value.petId, episodeId: value.episodeId, token: value.token, ...(typeof value.inviteId === 'string' ? { inviteId: value.inviteId } : {}), role: value.role, actorLabel: typeof value.actorLabel === 'string' ? value.actorLabel.slice(0, 20) : value.role === 'provider' ? '돌봄자' : '보호자', cursor: Number.isSafeInteger(value.cursor) ? Number(value.cursor) : 0 };
  } catch { return null; }
}

export function writeCareShareState(state: CareShareState): void {
  localStorage.setItem(CARE_SHARE_STATE_KEY, JSON.stringify(state));
}

export function clearCareShareState(): void {
  localStorage.removeItem(CARE_SHARE_STATE_KEY);
}

export function updateCareShareCursor(cursor: number): CareShareState | null {
  const state = readCareShareState();
  if (!state) return null;
  const next = { ...state, cursor: Math.max(state.cursor, cursor) };
  writeCareShareState(next);
  return next;
}

/** Encrypts only one care episode, its reusable care sheet, and the pet label. */
export async function createCareShareCiphertext(petId: string, episodeId: string, token: string): Promise<string> {
  const [pet, episode, passports] = await Promise.all([
    repository.get<Pet>('pets', petId),
    repository.get<CareEpisodeRecord>('careEpisodes', episodeId),
    repository.list<CarePassportRecord>('carePassports'),
  ]);
  if (!pet || !episode || episode.petId !== petId) throw new Error('돌봄 건을 찾지 못했어요.');
  const passport = passports.find((item) => item.petId === petId);
  const packet: CareSharePacket = { schemaVersion: 1, pet: { id: pet.id, name: pet.name, species: pet.species }, ...(passport ? { passport } : {}), episode };
  return toBase64(await encryptBackup(JSON.stringify(packet), token));
}

/** Imports no health history, attachments, costs, visits, or other care episodes. */
export async function applyCareShareCiphertext(ciphertext: string, token: string, expectedEpisodeId: string, author: CareShareAuthor): Promise<CareEpisodeRecord> {
  if (author.role !== 'guardian' && author.role !== 'provider') throw new Error('돌봄 기록의 작성자 역할을 확인하지 못했어요.');
  const plain = await decryptBackup(fromBase64(ciphertext), token);
  const packet = JSON.parse(new TextDecoder().decode(plain)) as Partial<CareSharePacket>;
  if (packet.schemaVersion !== 1 || !packet.pet || !packet.episode || packet.episode.id !== expectedEpisodeId || packet.episode.petId !== packet.pet.id) throw new Error('이 돌봄 링크의 내용을 읽지 못했어요.');
  if (author.role === 'provider') {
    const current = await repository.get<CareEpisodeRecord>('careEpisodes', packet.episode.id);
    if (!current) throw new Error('보호자 원장에 없는 돌봄 건은 합칠 수 없어요.');
    const merged = mergeProviderCareEpisodeUpdate(current, packet.episode, author.label, author.createdAt);
    await repository.put('careEpisodes', { ...merged, updatedAt: current.updatedAt });
    const saved = await repository.get<CareEpisodeRecord>('careEpisodes', merged.id);
    if (!saved) throw new Error('가져온 돌봄 기록을 확인하지 못했어요.');
    return saved;
  }
  const currentPet = await repository.get<Pet>('pets', packet.pet.id);
  if (!currentPet) await repository.put('pets', { id: packet.pet.id, name: packet.pet.name.slice(0, 20), species: packet.pet.species, sex: 'unknown', neutered: false, birthYearOnly: false, conditions: [], sortOrder: Date.now() });
  if (packet.passport?.petId === packet.pet.id) await repository.put('carePassports', packet.passport);
  // The feed cursor is the cross-device order. LocalRepository stamps a local
  // updatedAt on import, so comparing device clocks here can discard the next
  // valid packet from the same ordered feed.
  const currentEpisode = await repository.get<CareEpisodeRecord>('careEpisodes', packet.episode.id);
  await repository.put('careEpisodes', { ...packet.episode, updatedAt: currentEpisode?.updatedAt ?? packet.episode.updatedAt });
  const saved = await repository.get<CareEpisodeRecord>('careEpisodes', packet.episode.id);
  if (!saved) throw new Error('가져온 돌봄 기록을 확인하지 못했어요.');
  return saved;
}
